
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';

export function useTeamDelete() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!user) throw new Error('User not authenticated');
      
      // First check if user is admin or superadmin
      const { data: roleData, error: roleError } = await supabase
        .rpc('get_team_member_role', { 
          team_id: id,
          user_id: user.id
        });
        
      if (roleError) throw roleError;
        
      if (roleData !== 'admin' && roleData !== 'superadmin') {
        throw new Error('You do not have permission to delete this team');
      }
      
      // First delete team_members
      const { error: membersError } = await supabase
        .from('team_members')
        .delete()
        .eq('team_id', id);
      
      if (membersError) throw membersError;
      
      // Then delete the team
      const { error } = await supabase
        .from('teams')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      toast({
        title: "Team deleted",
        description: "The team has been deleted successfully.",
      });
    },
    onError: (error: any) => {
      console.error('Delete team error:', error);
      toast({
        title: "Error deleting team",
        description: error.message || "There was an error deleting the team.",
        variant: "destructive",
      });
    }
  });
}
